import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Link,
  IconButton,
  Divider,
  alpha,
} from '@mui/material';
// import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { MuiOtpInput } from 'mui-one-time-password-input';
import {
  BACKGROUND_GRADIENT_TRANSPARENT,
  BUTTON_BG_COLOR,
} from './PropertySpotlightDialog';

interface OTPVerificationProps {
  onVerify: (otp: string) => Promise<void>;
  handleResend: () => void;
  onBack?: () => void;
  isLoading: boolean;
  timeLeft: number;
  isResendDisabled: boolean;
}

const OTPVerification: React.FC<OTPVerificationProps> = ({
  onVerify,
  handleResend,
  isLoading,
  timeLeft,
  isResendDisabled,
  onBack,
}) => {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');

  const handleVerify = async () => {
    if (otp.length === 0) {
      setError('OTP is required');
      return;
    } else if (otp.length !== 4) {
      setError('OTP must be 4 digits');
      return;
    }

    setError('');
    await onVerify(otp);
  };

  const handleChange = (value: string) => {
    // Only allow numbers and max 4 digits
    const cleaned = value.replace(/\D/g, '').slice(0, 4);
    setOtp(cleaned);
    setError('');
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatTime = (seconds: number) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${mins}:${secs}s`;
  };

  return (
    <Box
      sx={{
        pb: 3,
        overflowY: 'auto',
        borderRadius: { xs: 2, sm: 3 },
        flex: 1,
        background: BACKGROUND_GRADIENT_TRANSPARENT,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 3,
          py: 1,
          mb: 2,
          textAlign: 'center',
          background:
            'linear-gradient(to right, #B1CAFF, #ffffff, #ffffff, #ffffff)',
          borderBottom: '1px solid',
          borderColor: (theme) => alpha(theme.palette.primary.light, 0.1),
        }}
      >
        <IconButton
          onClick={onBack}
          disabled={isLoading}
          sx={{ mr: 0.5, p: 0.5, pl: 0 }}
          aria-label='Go back'
        >
          <KeyboardBackspaceIcon sx={{ color: 'primary.light' }} />
        </IconButton>
        <Typography fontWeight={400} color={'primary.light'}>
          Back to Property
        </Typography>
      </Box>

      <Box sx={{ px: 2 }}>
        <Typography
          variant='h6'
          fontWeight={500}
          sx={{ mb: 2, textAlign: 'center' }}
        >
          Verify Your Code
        </Typography>
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ mb: 2, textAlign: 'center' }}
        >
          Enter the 4-digit code we just texted you.
        </Typography>

        <Box textAlign={'center'} my={1}>
          <Box
            component={'img'}
            src={'https://miles-cdn.qrbuddy.ai/static/img_shild_protection.png'}
            sx={{
              objectFit: 'cover',
              height: '80px',
              // maxWidth: '70%',
              // mx: 'auto',
            }}
          />
        </Box>

        <Stack direction='column' spacing={2} alignItems='center' mt={3}>
          <MuiOtpInput
            value={otp}
            onChange={(value) => handleChange(value)}
            length={4}
            autoFocus
            validateChar={(char) => /^[0-9]$/.test(char)}
            sx={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
            }}
            TextFieldsProps={{
              inputProps: {
                inputMode: 'numeric',
                type: 'tel',
              },
              sx: {
                width: '54px',
                height: '54px',
                input: {
                  textAlign: 'center',
                  fontSize: '18px',
                  backgroundColor: '#EDF1F7',
                  borderRadius: '12px',
                  border: `1px solid`,
                  borderColor: 'grey.400',
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                },
              },
            }}
          />

          {!!error && (
            <Typography variant='caption' color='error'>
              {error}
            </Typography>
          )}
        </Stack>

        <Divider
          sx={{ mt: 3, mb: 2, borderColor: 'rgba(102, 126, 234, 0.2)' }}
        />

        <Button
          fullWidth
          variant='contained'
          onClick={handleVerify}
          //disabled={isLoading || otp.length !== 4}
          sx={{
            py: 1.5,
            color: '#ffffff',
            // background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            background: BUTTON_BG_COLOR,
            textTransform: 'none',
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} sx={{ color: '#ffffff' }} />
          ) : (
            'Submit Code'
          )}
        </Button>

        <Stack
          direction='row'
          spacing={2}
          justifyContent='center'
          alignItems='center'
          mt={3}
        >
          {/* <Typography variant='caption' fontWeight='500' color='grey.600'>
            {t('remaining_time')}{' '}
            <Typography
              component='span'
              variant='caption'
              sx={{
                fontWeight: 500,
                color: timeLeft === 0 ? 'grey.400' : 'primary.light',
              }}
            >
              {formatTime(timeLeft)}
            </Typography>
          </Typography> */}

          <Typography variant='caption' fontWeight='500' color='grey.600'>
            Didn't receive code?{' '}
            <Link
              component='button'
              variant='caption'
              underline='none'
              disabled={isResendDisabled}
              onClick={() => {
                handleResend();
              }}
              sx={{
                fontWeight: 500,
                color: isResendDisabled ? 'grey' : 'primary.light',
                cursor: isResendDisabled ? 'not-allowed' : 'pointer',
                '&:hover': {
                  color: isResendDisabled ? 'grey' : '#1890FF',
                },
                '&.Mui-disabled': {
                  color: 'grey',
                },
              }}
            >
              Resend {timeLeft ? `in ${formatTime(timeLeft)}` : ''}
            </Link>
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default OTPVerification;
